import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { templates as tplApi } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Zap, Droplet, Hammer, Wind, Paintbrush, Leaf, Home, Sparkles, Ruler, Fence,
  ArrowRight, Loader2,
} from "lucide-react";

const ICONS = {
  zap: Zap, droplet: Droplet, hammer: Hammer, wind: Wind, paintbrush: Paintbrush,
  leaf: Leaf, home: Home, sparkles: Sparkles, ruler: Ruler, fence: Fence,
};

export default function Templates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    tplApi.list()
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, []);

  const start = async (id) => {
    setBusyId(id);
    try {
      const { data } = await tplApi.createQuote(id);
      toast.success("Quote drafted from template");
      nav(`/quotes/${data.quote_id}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start from template");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#2563EB]" size={28} />
    </div>
  );

  return (
    <div data-testid="templates-page" className="p-5 sm:p-8 max-w-7xl mx-auto">
      <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">
        Trade templates
      </div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-[#001F3F]"
          style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
        A starting point for the work you quote.
      </h1>
      <p className="mt-3 text-sm text-[#64748B] max-w-2xl">
        Pick a template that matches your trade — we&apos;ll pre-fill line items, tax and terms.
        Every price stays editable until you send.
      </p>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => {
          const Icon = ICONS[t.icon] || Hammer;
          return (
            <div
              key={t.id}
              data-testid={`template-card-${t.id}`}
              className="rounded-lg border border-slate-200 bg-white p-6 flex flex-col hover:-translate-y-1 hover:shadow-md transition-transform duration-200"
            >
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-md bg-[#EFF6FF] text-[#2563EB] flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-[#64748B]">
                  {t.trade}
                </div>
              </div>
              <h3 className="mt-4 text-lg font-bold tracking-tight text-[#001F3F]"
                  style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
                {t.name}
              </h3>
              <p className="mt-1 text-sm text-[#64748B] leading-relaxed flex-1">{t.tagline}</p>
              <Button
                data-testid={`template-use-${t.id}`}
                onClick={() => start(t.id)}
                disabled={busyId === t.id}
                className="mt-5 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-10 self-start"
              >
                {busyId === t.id ? "Starting…" : "Use this template"}
                <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-10 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
        <div className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">
          Don&apos;t see your trade?
        </div>
        <h3 className="mt-2 text-xl font-bold text-[#001F3F]"
            style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
          Start blank and speak the job.
        </h3>
        <p className="mt-1 text-sm text-[#64748B]">
          The AI voice-to-quote flow works for any field service.
        </p>
        <Button onClick={() => nav("/quotes/new")}
                className="mt-4 rounded-full bg-[#001F3F] hover:bg-slate-800 text-white h-10">
          New blank quote <ArrowRight size={14} className="ml-1" />
        </Button>
      </div>
    </div>
  );
}
